import { useCallback, useEffect, useState } from 'react';
import type { Place } from '../types';

const KEY = 'hy_saved_v3';

/**
 * Saved places, persisted to localStorage.
 *
 * The whole record is stored, not just the id: v2 kept ids and then looked them
 * up in a global that no page ever loaded, so opening Saved Places threw as soon
 * as you had one favourite.
 */
export function useSaved() {
    const [saved, setSaved] = useState<Place[]>(() => {
        try {
            const raw = localStorage.getItem(KEY);
            const parsed = raw ? JSON.parse(raw) : [];
            return Array.isArray(parsed) ? (parsed as Place[]) : [];
        } catch {
            return [];
        }
    });

    useEffect(() => {
        try { localStorage.setItem(KEY, JSON.stringify(saved)); } catch { /* quota */ }
    }, [saved]);

    const toggle = useCallback((place: Place) => {
        setSaved((cur) => (cur.some((p) => p.id === place.id)
            ? cur.filter((p) => p.id !== place.id)
            : [...cur, place]));
    }, []);

    const isSaved = useCallback((id: string) => saved.some((p) => p.id === id), [saved]);

    return { saved, toggle, isSaved };
}
